import type * as React from "react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {useFastlyQuery, type Persist} from "@khan/data-gql";
import type {GAEModule, GqlOperation} from "@khan/data-gql-core";

/**
 * Options for Fastly queries.
 */
type Props<TData, TVariables extends Record<any, any>> = {
    /**
     * The Fastly query to be performed.
     */
    query: GqlOperation<TData, TVariables>;
    /**
     * When true, regardless of the cache state, the query will not be
     * executed. The current cache state will be returned, which will be
     * {status: "loading"} if the query is not in the cache.
     */
    skip?: boolean | null | undefined;
    /**
     * The variables your query needs to execute.
     */
    variables?: TVariables;
    /**
     * The caching policy to attach to this query's result.
     * Defaults to Persist.UntilPublish.
     */
    cachingPolicy?: Persist;
    /**
     * Override the default module.
     * Defaults to the value provided by GqlRouter.
     */
    module?: GAEModule;
    /**
     * Override the default locale.
     * Useful for targetting a different localized topic tree, for example.
     * Defaults to the value provided by GqlRouter.
     */
    targetLocale?: string;
    /**
     * Override the default curriculum.
     * Defaults to the value provided by GqlRouter.
     */
    curriculum?: string;
    /**
     * The children to render with the query result.
     */
    // @ts-expect-error - TS2344
    children: (arg1: Result<TData>) => React.ReactNode;
};

/**
 * Execute a Fastly-cached query and render children with the result.
 *
 * NOTE: If you're writing a functional component, please use `useFastlyQuery`
 * hook. This component is only for supporting legacy React components that
 * cannot use hooks.
 */
const FastlyQuery = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    props: Props<TData, TVariables>,
): React.ReactElement => {
    const {query, children, ...options} = props;
    const result = useFastlyQuery<TData, TVariables>(query, options);
    // @ts-expect-error - TS2322 - Type 'ReactNode' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
    return children(result);
};
export default FastlyQuery;
